import type { FormAssignment } from "@sdt/render-form-engine";
import type { VariableFileName } from "./variables";

export type GlobalType =
  | "APPLICANT"
  | "NODE_EXECUTION_START"
  | "NODE_EXECUTION_FINISH"
  | "CURRENT_TIME"
  | "HOLDER"
  | "EXECUTOR"
  | "IMPERSONATING"
  | "PERSON_TYPE"
  | "COMPANY_FIELD"
  | "LEGAL_INFO"
  | "LAST_OBSERVATION_DATE"
  | "FORMALITY_REQUEST";

export type RefName =
  | "initializer-node"
  | "if-node"
  | "calculated-node"
  | "form-node"
  | "switch-node"
  | "assign-group-node"
  | "data-center-node"
  | "expedient-node"
  | "revision-node"
  | "document-generator-node"
  | "sign-document-node"
  | "payment-node"
  | "multithread-node"
  | "wait-node"
  | "notification-node"
  | "interoperability-node"
  | "n8n-node"
  | "resolution-node"
  | "merge-node"
  | "associate-user-node"
  | "confirm-associate-user-node"
  | "impersonation-node"
  | "holder-node"
  | "processor-node"
  | "finish-node"
  | "conditional-grouping-node"
  | "subflow-node"
  | "holder-company-node";
export const NodeTypes = {
  INITIALIZER: "initializer-node",
  IF: "if-node",
  CALCULATED: "calculated-node",
  FORM: "form-node",
  CORRECTION: "revision-node",
  SWITCH: "switch-node",
  SUBFLOW: "subflow-node",
  ASSIGN_GROUP: "assign-group-node",
  DATA_CENTER: "data-center-node",
  EXPEDIENTE: "expedient-node",
  DOC_GENERATOR: "document-generator-node",
  SIGN_DOCUMENT: "sign-document-node",
  RESOLUTION: "resolution-node",
  N8N: "n8n-node",
  PAYMENT: "payment-node",
  MULTITHREAD: "multithread-node",
  WAIT: "wait-node",
  NOTIFICATION: "notification-node",
  INTEROPERABILITY: "interoperability-node",
  MERGE: "merge-node",
  ASSOCIATE_USER: "associate-user-node",
  CONFIRM_ASSOCIATE_USER: "confirm-associate-user-node",
  IMPERSONATION: "impersonation-node",
  HOLDER: "holder-node",
  HOLDER_COMPANY: "holder-company-node",
  PROCESSOR: "processor-node",
  END: "finish-node",
  VARIABLE_REPLACEMENT: "conditional-grouping-node",
} as const;

export type Edge = {
  id?: string;
  name?: string;
  source?: string | null;
  target?: string | null;
  source_handler: string;
  target_handler: string;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type FilterDataType = "DATE" | "BOOLEAN" | "NUMBER" | "TEXT" | "STRING";

export type ConditionEnum =
  | "EXIST"
  | "NOT_EXIST"
  | "IS_EMPTY"
  | "IS_NOT_EMPTY"
  | "EQUALS"
  | "NOT_EQUALS"
  | "CONTAINS"
  | "NOT_CONTAINS"
  | "STARTS_WITH"
  | "ENDS_WITH"
  | "MATCHES"
  | "NOT_MATCHES"
  | "GREATER_THAN"
  | "LESS_THAN"
  | "GREATER_THAN_OR_EQUAL"
  | "LESS_THAN_OR_EQUAL"
  | "IS_AFTER"
  | "IS_BEFORE"
  | "IS_IN_OR_AFTER"
  | "IS_IN_OR_BEFORE"
  | "IS_TRUE"
  | "IS_FALSE"
  | "LENGTH_EQUAL"
  | "LENGTH_NOT_EQUAL"
  | "LENGTH_GREATER_THAN"
  | "LENGTH_LESS_THAN"
  | "LENGTH_GREATER_THAN_OR_EQUAL"
  | "LENGTH_LESS_THAN_OR_EQUAL"
  | "NOT_STARTS_WITH"
  | "NOT_ENDS_WITH"
  | "BETWEEN"
  | "ANY"
  | "AFTER"
  | "BEFORE"
  | "AFTER_EQUALS"
  | "BEFORE_EQUALS";

export type SerializeCandidate = {
  name?: string;
  pivot: {
    order: number;
  };
  global_type: string;
  source_connections?: Array<{
    source_node_id: string;
    output_name: string;
    field_id: string;
  }>;
};

export type Candidate = {
  order: string;
  name?: string;
  connection: string;
  output_name: string;
  field?: string;
  global_type: string;
};

export type Grouping = {
  sub_type: string;
  data_type: string;
  on_missing_value_behavior: string;
  default_value: string | null;
  candidates: Array<SerializeCandidate> | Array<Candidate>;
  display_name: string;
  config?: Record<string, string | boolean | number | undefined> | null;
};

export type Filter = {
  name: string;
  condition: ConditionEnum;
  value: string | null | Array<string>;
  id?: string;
  type?: FilterDataType;
  field?: string;
  output_name?: string;
  connection?:
    | string
    | {
        id: string;
        target_node_id: string | null;
        source_node_id: string | null;
        ref_name: string;
        display_name: string;
        field_id?: string | null;
        output_name?: string;
        field?: {
          id: string;
          name: string;
          type: string;
          is_array?: boolean;
          label?: string;
          form_step_id?: string;
          form_section_id?: string;
          config?: Record<string, string | boolean | number | undefined>;
        };
      };
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME"
    | "HOLDER"
    | "EXECUTOR"
    | "IMPERSONATING"
    | "PERSON_TYPE"
    | "COMPANY_FIELD"
    | "LEGAL_INFO"
    | "LAST_OBSERVATION_DATE";
};

export type FilterGroup = {
  id: string;
  filter_node_input_id: string;
  conditions: Array<Filter>;
};

export type InputVariableReference = Omit<Input[number], "name">;

export type InputColumnsValue = Record<
  string,
  string | boolean | InputVariableReference
>;

export type Input = Array<{
  id?: string;
  name: string;
  is_sandbox?: boolean;
  node_input_id?: string;
  value:
    | string
    | number
    | null
    | boolean
    | Array<Filter>
    | Array<string>
    | Array<Array<string>>
    | VariableFileName
    | InputColumnsValue;
  type: string;
  connection?: string;
  field_id?: string; // for variables inside a form
  field?: string; // for variables (legacy format) - UUID for form nodes, endpoint field key for interoperability nodes
  output_name?: string; // for interoperability node outputs - contains the endpoint field key (e.g., "last_name")
  reference_key?: string; // for expediente node - identifies which entity field this input belongs to
  order?: number;
  //if
  conditions?: Conditions;
  calculations?: Calculations;
  grouping?: Grouping;
  pivot?: {
    order: number;
  };
  references?: Array<{
    name: string;
    type: string;
    value?:
      | string
      | number
      | null
      | boolean
      | Array<string>
      | Array<Array<string>>
      | Array<Filter>
      | VariableFileName
      | InputColumnsValue;
    connection?: string;
    field?: string;
    field_id?: string;
    output_name?: string;
    global_type?: GlobalType;
  }>;
  source_connections?: Array<{
    target_node_input_id?: string;
    field: {
      id: string;
      name: string;
      type: string;
      is_array?: boolean;
      label?: string;
      form_step_id?: string;
      form_section_id?: string;
      config?: Record<string, string | boolean | number | undefined>;
    };
    source_node_id: string;
    output_name?: string;
    field_id?: string;
    source_node_output_id?: string;
  }>;
  // data center
  label?: string;
  filters?: FilterGroup | Array<Filter>;
  test_node_input?: {
    id: string;
    value: null | string | number | boolean;
    node_input_id: string;
  };
  documents?: Array<{ id?: string; inputs: Input }>;
  // interoperability node nested inputs
  inputs?: {
    name: string;
    type: string;
    value: string | number | null | boolean;
    connection?: string;
    field?: string;
    output_name?: string;
    source_connections?: Array<{
      field: {
        id: string;
        name: string;
        type: string;
        config?: Record<string, string | boolean | number | undefined>;
      };
      source_node_id: string;
      output_name?: string;
      field_id?: string;
      source_node_output_id?: string;
    }>;
    field_id?: string;
  };
  global_type?: GlobalType;
}>;

export type Connections = Record<
  string,
  {
    display_name: string;
    target_node?: string | null;
  }
>;

export type Conditions = Array<{
  id?: string;
  name: string;
  type: string;
  operator?: string;
  inputs: Input;
  input?: Input[number];
  optional_input?: Input[number];
  connection?: string;
  test_node_input?: {
    id: string;
    value: null | string | number | boolean;
    node_input_id: string;
  };
  source_connections?: Array<{
    field: {
      id: string;
    };
    source_node_id: string;
    output_name?: string;
    field_id?: string;
    source_node_output_id?: string;
  }>;
}>;

type Calculations = Array<{
  operation: string;
  date_get_format?: string | null;
  date_to_string_format?: string | null;
  inputs: Input & {
    order?: number;
  };
}>;

export type ApiConnection = Array<{
  id: string;
  target_node_id: string | null;
  source_node_id: string | null;
  ref_name: string;
  display_name: string;
  field_id?: string | null;
}>;

export type Expiration = {
  id: string;
  display_name: string;
  available_days: ("mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun")[];
  node_id: string;
  created_at: string;
  updated_at: string;
  limit_unit: "minute" | "hour" | "day" | "month" | "year";
  limit_amount: number;
  node_time_extension?: {
    id: string;
    extension_amount: number;
    extension_unit: "minute" | "hour" | "day" | "month" | "year";
    leader_required: boolean;
  };
};

export interface UpdateNodeBody {
  flow_id: string;
  ref_name: RefName;
  display_name: string;
  type: string;
  order: number;
  position_x: number;
  position_y: number;
  manual_continue?: boolean;
  edge?: Edge | null;
  edges?: Array<{
    source: string;
    source_handler: string;
    target_handler: string;
    target_input_name: string;
  }>;
  inputs?: Input;
  connections?: Connections;
}

export interface ApiNode {
  id?: string;
  ref_name: RefName;
  display_name: string;
  position_x: number;
  position_y: number;
  order: number;
  type:
    | "IF_CONDITION"
    | "SWITCH_CONDITION"
    | "GENERAL"
    | "WEBHOOK"
    | "FORM"
    | "REVISION"
    | "DOCUMENT_GENERATOR"
    | "DIGITAL_SIGNATURE"
    | "PAYMENT"
    | "DATA_CENTER"
    | "EXPEDIENTE"
    | "MULTITHREAD"
    | "WAIT"
    | "NOTIFICATION"
    | "INTEROPERABILITY"
    | "SIGN_DOCUMENT"
    | "N8N"
    | "MERGE"
    | "SUBFLOW"
    | "CONDITIONAL_GROUPING"
    | "ASSOCIATE_USER"
    | "CONFIRM_ASSOCIATE_USER"
    | "RESOLUTION";
  edge?: Edge;
  edges?: Edge[];
  inputs?: Input;
  expiration?: Expiration;
  manual_continue?: boolean;
  //for switch/if node
  target_connections?: ApiConnection;
  connections?: Connections;
  conditions?: Conditions;
  calculations?: Calculations;
  form_assignments?: FormAssignment[];
}
/**
 * Datos que lleva un nodo del canvas.
 *
 * Es un `type` y no una `interface` a propósito: React Flow exige que el data
 * de un nodo sea asignable a `Record<string, unknown>`, y TypeScript solo
 * infiere ese index signature implícito para alias de tipo, no para interfaces.
 * Con `interface` cada lectura de `node.data` necesitaba `as unknown as NodeData`.
 */
export type NodeData = {
  type: string;
  node_type: string;
  displayName: string;
  hide_outputs?: boolean;
  inputs: Input;
  order: number;
  edge: {
    source: string;
    source_handler: string;
    target_handler: string;
  } | null;
  edges?: Edge[];
  connections?: Connections;
  outputs?: { id: string; label: string }[];
  conditions?: Conditions;
  calculations?: Calculations;
  manual_continue?: boolean;
};

/** Datos de un edge del canvas. `type` y no `interface`, por lo mismo que NodeData. */
export type EdgeData = {
  source: {
    id: string;
    handle: string;
  };
  target: {
    id: string;
    handle: string;
  };
};

export interface NodeExecutionTimestamps {
  start_timestamp: string;
  end_timestamp?: string;
  status?: "success" | "error";
  error_message?: string;
}
